/**
 * Errors of the machines domain, and the error codes the API sends for them.
 *
 * No dependencies: the frontend imports the codes (via `/schemas`) without
 * pulling in the repository and its Supabase client.
 *
 * The `message` of these errors is **user-facing**: the API returns it as the
 * `error` of the response, so it is written in German and contains nothing but
 * the VIN the user entered. Keep it that way — never pass a Postgres or driver
 * message into one of these constructors. Errors of an unknown kind get a
 * fixed message from the handler instead.
 */

/** Machine-readable `code` in error responses of the machines API. */
export const MachineErrorCodes = {
  /** No (non-deleted) machine with that VIN */
  NotFound: "MACHINE_NOT_FOUND",
  /** The machine belongs to another org already */
  LinkedToOtherOrg: "MACHINE_LINKED_TO_OTHER_ORG",
  /** The machine belongs to the target org already */
  AlreadyInOrg: "MACHINE_ALREADY_IN_ORG",
} as const

export type MachineErrorCode =
  (typeof MachineErrorCodes)[keyof typeof MachineErrorCodes]

/** Thrown when linking a machine whose VIN is unknown (or deleted). */
export class MachineNotFoundError extends Error {
  readonly code = MachineErrorCodes.NotFound

  constructor(message: string) {
    super(message)
    this.name = "MachineNotFoundError"
  }
}

/** Thrown when linking a machine that belongs to another org already. */
export class MachineLinkedToOtherOrgError extends Error {
  readonly code = MachineErrorCodes.LinkedToOtherOrg

  constructor(message: string) {
    super(message)
    this.name = "MachineLinkedToOtherOrgError"
  }
}

/** Thrown when linking a machine that belongs to the target org already. */
export class MachineAlreadyInOrgError extends Error {
  readonly code = MachineErrorCodes.AlreadyInOrg

  constructor(message: string) {
    super(message)
    this.name = "MachineAlreadyInOrgError"
  }
}
